import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI, documentAPI, adminV2API } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  Users, 
  Car, 
  DollarSign, 
  TrendingUp, 
  Tag, 
  Loader2,
  Plus,
  Trash2,
  CheckCircle,
  XCircle,
  Clock,
  BarChart3,
  UserCheck,
  MapPin,
  Settings,
  Key,
  Zap,
  FileText,
  Eye,
  Download,
  Ban,
  ShieldOff,
  ShieldCheck,
  AlertTriangle,
  MoreVertical,
  X,
  UserCog,
  Crown,
  History
} from 'lucide-react';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [rides, setRides] = useState([]);
  const [promos, setPromos] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [config, setConfig] = useState(null);
  const [pendingDocuments, setPendingDocuments] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [showCreatePromo, setShowCreatePromo] = useState(false);
  const [showAdminRegister, setShowAdminRegister] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Admin Management State
  const [admins, setAdmins] = useState([]);
  const [adminActivity, setAdminActivity] = useState([]);
  const [adminActionModal, setAdminActionModal] = useState({ show: false, type: '', admin: null });
  const [grantAdminSearch, setGrantAdminSearch] = useState('');
  const [grantAdminUserId, setGrantAdminUserId] = useState('');
  const [revokeReason, setRevokeReason] = useState('');

  // Promo form
  const [promoForm, setPromoForm] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: 10,
    max_uses: 100,
    min_ride_amount: 0,
    max_discount: null,
    description: ''
  });

  // Admin registration form
  const [adminForm, setAdminForm] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: '',
    secret_key: ''
  });

  useEffect(() => {
    checkAdminAccess();
  }, [user]);

  const checkAdminAccess = async () => {
    // Check if user from context is admin
    if (user && user.is_admin) {
      try {
        await loadData();
        setIsAdmin(true);
        return;
      } catch (error) {
        console.error('Error loading admin data:', error);
      }
    }
    
    // If no user in context, check localStorage
    const savedUser = localStorage.getItem('reddi_user');
    if (savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        if (parsedUser.is_admin) {
          await loadData();
          setIsAdmin(true);
          return;
        }
      } catch (e) {
        console.error('Error parsing saved user:', e);
      }
    }
    
    setLoading(false);
  };

  const handleAdminRegister = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await adminAPI.registerAdmin(adminForm);
      toast.success(response.data.message || 'Admin access granted!');
      // Auto-login with the new token
      localStorage.setItem('reddi_token', response.data.token);
      localStorage.setItem('reddi_user', JSON.stringify({
        id: response.data.user_id,
        email: adminForm.email,
        full_name: adminForm.full_name,
        user_type: response.data.user_type,
        is_admin: true
      }));
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to register admin');
    } finally {
      setSubmitting(false);
    }
  };

  const loadData = async () => {
    try {
      const [statsRes, usersRes, ridesRes, promosRes, payoutsRes, revenueRes, configRes, docsRes, adminsRes, activityRes] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getUsers(null, 20),
        adminAPI.getRides(null, 20),
        adminAPI.getPromos(),
        adminAPI.getPayouts(null, 20),
        adminAPI.getRevenue(30),
        adminAPI.getConfig(),
        documentAPI.getPendingDocuments(50).catch(() => ({ data: { documents: [] } })),
        adminV2API.getAdmins().catch(() => ({ data: { admins: [] } })),
        adminV2API.getAdminActivity(30).catch(() => ({ data: [] }))
      ]);
      
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setRides(ridesRes.data);
      setPromos(promosRes.data);
      setPayouts(payoutsRes.data);
      setRevenue(revenueRes.data);
      setConfig(configRes.data);
      setPendingDocuments(docsRes.data?.documents || []);
      setAdmins(adminsRes.data?.admins || []);
      setAdminActivity(activityRes.data || []);
    } catch (error) {
      console.error('Error loading admin data:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePromo = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await adminAPI.createPromo({
        ...promoForm,
        code: promoForm.code.toUpperCase(),
        max_discount: promoForm.max_discount || null
      });
      toast.success('Promo code created');
      setShowCreatePromo(false);
      setPromoForm({
        code: '',
        discount_type: 'percentage',
        discount_value: 10,
        max_uses: 100,
        min_ride_amount: 0,
        max_discount: null,
        description: ''
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create promo');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTogglePromo = async (promoId, isActive) => {
    try {
      await adminAPI.updatePromo(promoId, !isActive);
      toast.success(`Promo ${isActive ? 'deactivated' : 'activated'}`);
      loadData();
    } catch (error) {
      toast.error('Failed to update promo');
    }
  };

  const handleDeletePromo = async (promoId) => {
    if (!window.confirm('Delete this promo code?')) return;
    try {
      await adminAPI.deletePromo(promoId);
      toast.success('Promo deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete promo');
    }
  };

  const handleProcessPayout = async (payoutId, action) => {
    try {
      await adminAPI.processPayout(payoutId, action);
      toast.success(`Payout ${action}d`);
      loadData();
    } catch (error) {
      toast.error('Failed to process payout');
    }
  };

  const handleReviewDocument = async (documentId, action, reason = '') => {
    try {
      await documentAPI.reviewDocument(documentId, action, reason);
      toast.success(`Document ${action === 'approve' ? 'approved' : 'rejected'} successfully`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to review document');
    }
  };

  const handleRejectDocument = (documentId) => {
    const reason = window.prompt('Enter rejection reason:');
    if (reason && reason.trim()) {
      handleReviewDocument(documentId, 'reject', reason.trim());
    }
  };

  // User Management State
  const [userActionModal, setUserActionModal] = useState({ show: false, type: '', user: null });
  const [actionForm, setActionForm] = useState({ reason: '', duration_days: 7, notes: '' });
  const [processingAction, setProcessingAction] = useState(false);
  const [userFilter, setUserFilter] = useState('all');
  const [userSearch, setUserSearch] = useState('');

  // User Management Handlers
  const openUserAction = (type, user) => {
    setUserActionModal({ show: true, type, user });
    setActionForm({ reason: '', duration_days: 7, notes: '' });
  };

  const closeUserAction = () => {
    setUserActionModal({ show: false, type: '', user: null });
    setActionForm({ reason: '', duration_days: 7, notes: '' });
  };

  const handleSuspendUser = async () => {
    if (!actionForm.reason.trim()) {
      toast.error('Please provide a reason for suspension');
      return;
    }
    setProcessingAction(true);
    try {
      await adminV2API.suspendUser(userActionModal.user.id, actionForm.reason.trim(), actionForm.duration_days);
      toast.success(`User ${userActionModal.user.full_name} has been suspended for ${actionForm.duration_days} days`);
      closeUserAction();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to suspend user');
    } finally {
      setProcessingAction(false);
    }
  };

  const handleBanUser = async () => {
    if (!actionForm.reason.trim()) {
      toast.error('Please provide a reason for the ban');
      return;
    }
    setProcessingAction(true);
    try {
      await adminV2API.banUser(userActionModal.user.id, actionForm.reason.trim());
      toast.success(`User ${userActionModal.user.full_name} has been permanently banned`);
      closeUserAction();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to ban user');
    } finally {
      setProcessingAction(false);
    }
  };

  const handleReactivateUser = async () => {
    setProcessingAction(true);
    try {
      await adminV2API.reactivateUser(userActionModal.user.id, actionForm.notes.trim());
      toast.success(`User ${userActionModal.user.full_name} has been reactivated`);
      closeUserAction();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate user');
    } finally {
      setProcessingAction(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!actionForm.reason.trim()) {
      toast.error('Please provide a reason for deletion');
      return;
    }
    setProcessingAction(true);
    try {
      await adminV2API.deleteUser(userActionModal.user.id, actionForm.reason.trim());
      toast.success(`User ${userActionModal.user.full_name} has been permanently deleted`);
      closeUserAction();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setProcessingAction(false);
    }
  };

  // Admin Management Handlers
  const handleGrantAdmin = async () => {
    if (!grantAdminUserId) {
      toast.error('Please enter a user ID');
      return;
    }
    setProcessingAction(true);
    try {
      await adminV2API.grantAdmin(grantAdminUserId);
      toast.success('Admin access granted successfully');
      setGrantAdminUserId('');
      setGrantAdminSearch('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to grant admin access');
    } finally {
      setProcessingAction(false);
    }
  };

  const handleRevokeAdmin = async () => {
    if (!revokeReason.trim()) {
      toast.error('Please provide a reason for revoking admin access');
      return;
    }
    setProcessingAction(true);
    try {
      await adminV2API.revokeAdmin(adminActionModal.admin.id, revokeReason.trim());
      toast.success(`Admin access revoked from ${adminActionModal.admin.full_name || adminActionModal.admin.email}`);
      setAdminActionModal({ show: false, type: '', admin: null });
      setRevokeReason('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke admin access');
    } finally {
      setProcessingAction(false);
    }
  };

  const getAdminSourceBadge = (source) => {
    switch (source) {
      case 'environment':
        return { label: 'ENV', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' };
      case 'database':
        return { label: 'DB', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' };
      case 'both':
        return { label: 'ENV+DB', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' };
      default:
        return { label: 'Unknown', color: 'bg-slate-100 text-slate-700' };
    }
  };

  const getUserStatus = (user) => {
    if (user.status === 'banned' || user.is_banned) return { label: 'Banned', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' };
    if (user.status === 'suspended' || user.is_suspended) return { label: 'Suspended', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' };
    return { label: 'Active', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' };
  };

  const filteredUsers = users.filter(u => {
    const isSuspended = u.status === 'suspended' || u.is_suspended;
    const isBanned = u.status === 'banned' || u.is_banned;
    const matchesFilter = userFilter === 'all' || 
      (userFilter === 'drivers' && u.user_type === 'driver') ||
      (userFilter === 'riders' && u.user_type === 'rider') ||
      (userFilter === 'suspended' && isSuspended) ||
      (userFilter === 'banned' && isBanned);
    const matchesSearch = !userSearch || 
      u.full_name?.toLowerCase().includes(userSearch.toLowerCase()) ||
      u.email?.toLowerCase().includes(userSearch.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const viewDocument = async (documentId) => {
    try {
      const response = await documentAPI.downloadDocument(documentId);
      const { file_data, content_type, file_name } = response.data;
      
      // Create blob and open in new tab
      const byteCharacters = atob(file_data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: content_type });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Failed to load document');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-100 dark:bg-slate-900">
        <Loader2 className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  // Show admin registration form if not admin
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-slate-100 dark:bg-slate-900 flex items-center justify-center p-6">
        <Card className="w-full max-w-md bg-white dark:bg-slate-800">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-red-600 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Key className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-display uppercase">Admin Access</CardTitle>
            <p className="text-slate-500 mt-2">
              Register as admin with secret key
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAdminRegister} className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Full Name</label>
                <Input
                  value={adminForm.full_name}
                  onChange={(e) => setAdminForm({ ...adminForm, full_name: e.target.value })}
                  placeholder="Admin User"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Email</label>
                <Input
                  type="email"
                  value={adminForm.email}
                  onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })}
                  placeholder="admin@example.com"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Phone</label>
                <Input
                  value={adminForm.phone}
                  onChange={(e) => setAdminForm({ ...adminForm, phone: e.target.value })}
                  placeholder="5551234567"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Password</label>
                <Input
                  type="password"
                  value={adminForm.password}
                  onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })}
                  placeholder="••••••••"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Admin Secret Key</label>
                <Input
                  type="password"
                  value={adminForm.secret_key}
                  onChange={(e) => setAdminForm({ ...adminForm, secret_key: e.target.value })}
                  placeholder="Enter admin secret key"
                  required
                />
              </div>
              <Button type="submit" disabled={submitting} className="w-full bg-red-600 hover:bg-red-700">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Register as Admin'}
              </Button>
            </form>
            <div className="mt-4 text-center">
              <Button variant="link" onClick={() => navigate('/')}>
                Back to App
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'admins', label: 'Admins', icon: Crown, badge: admins.length || null },
    { id: 'rides', label: 'Rides', icon: MapPin },
    { id: 'documents', label: 'Documents', icon: FileText, badge: pendingDocuments.length || null },
    { id: 'promos', label: 'Promo Codes', icon: Tag },
    { id: 'payouts', label: 'Payouts', icon: DollarSign },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-900" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-slate-900 text-white p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-600 rounded-xl flex items-center justify-center">
                <Car className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-2xl font-display font-bold uppercase tracking-tight">
                  REDDI Admin
                </h1>
                <p className="text-slate-400 text-sm">Management Dashboard</p>
              </div>
            </div>
            <Button variant="outline" onClick={() => navigate('/')}>
              Back to App
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-1 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 font-medium text-sm transition-colors border-b-2 ${
                  activeTab === tab.id
                    ? 'text-red-600 border-red-600'
                    : 'text-slate-500 border-transparent hover:text-slate-700'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                {tab.badge && (
                  <span className="ml-1 px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-full">
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto p-6">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-white dark:bg-slate-800">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Total Users</p>
                      <p className="text-3xl font-display font-bold text-slate-900 dark:text-white">
                        {stats?.total_users || 0}
                      </p>
                    </div>
                    <Users className="w-10 h-10 text-blue-500" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white dark:bg-slate-800">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Completed Rides</p>
                      <p className="text-3xl font-display font-bold text-slate-900 dark:text-white">
                        {stats?.completed_rides || 0}
                      </p>
                    </div>
                    <Car className="w-10 h-10 text-green-500" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white dark:bg-slate-800">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Total Revenue</p>
                      <p className="text-3xl font-display font-bold text-slate-900 dark:text-white">
                        ${stats?.total_revenue?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                    <DollarSign className="w-10 h-10 text-amber-500" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-red-500 to-red-600 border-0">
                <CardContent className="p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm opacity-80">Reddi Earnings</p>
                      <p className="text-3xl font-display font-bold">
                        ${stats?.reddi_earnings?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                    <TrendingUp className="w-10 h-10 opacity-80" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Revenue Chart Placeholder */}
            <Card className="bg-white dark:bg-slate-800">
              <CardHeader>
                <CardTitle>Revenue (Last 30 Days)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl">
                    <p className="text-sm text-slate-500">Total Rides</p>
                    <p className="text-xl font-bold text-slate-900 dark:text-white">
                      {revenue?.total_rides || 0}
                    </p>
                  </div>
                  <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl">
                    <p className="text-sm text-slate-500">Total Fares</p>
                    <p className="text-xl font-bold text-slate-900 dark:text-white">
                      ${revenue?.total_fares?.toFixed(2) || '0.00'}
                    </p>
                  </div>
                  <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl">
                    <p className="text-sm text-slate-500">Total Tips</p>
                    <p className="text-xl font-bold text-slate-900 dark:text-white">
                      ${revenue?.total_tips?.toFixed(2) || '0.00'}
                    </p>
                  </div>
                  <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl">
                    <p className="text-sm text-slate-500">Reddi Revenue</p>
                    <p className="text-xl font-bold text-green-600">
                      ${revenue?.reddi_revenue?.toFixed(2) || '0.00'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            {/* Users Header with Filters */}
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white">User Management</h2>
                <p className="text-sm text-slate-500">{stats?.total_users || 0} total users</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {['all', 'drivers', 'riders', 'suspended', 'banned'].map((filter) => (
                  <button
                    key={filter}
                    onClick={() => setUserFilter(filter)}
                    className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                      userFilter === filter
                        ? 'bg-red-600 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300'
                    }`}
                    data-testid={`user-filter-${filter}`}
                  >
                    {filter.charAt(0).toUpperCase() + filter.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Search */}
            <div className="max-w-sm">
              <Input
                placeholder="Search by name or email..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                className="bg-white dark:bg-slate-800"
                data-testid="user-search-input"
              />
            </div>

            {/* Users Table */}
            <Card className="bg-white dark:bg-slate-800">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200 dark:border-slate-700">
                        <th className="text-left p-4 text-sm font-medium text-slate-500">User</th>
                        <th className="text-left p-4 text-sm font-medium text-slate-500">Type</th>
                        <th className="text-left p-4 text-sm font-medium text-slate-500">Status</th>
                        <th className="text-left p-4 text-sm font-medium text-slate-500">Joined</th>
                        <th className="text-left p-4 text-sm font-medium text-slate-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((u) => {
                        const status = getUserStatus(u);
                        return (
                          <tr key={u.id} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700/50">
                            <td className="p-4">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-slate-200 dark:bg-slate-600 flex items-center justify-center">
                                  <span className="text-sm font-semibold text-slate-600 dark:text-slate-300">
                                    {u.full_name?.charAt(0)?.toUpperCase() || '?'}
                                  </span>
                                </div>
                                <div>
                                  <p className="font-medium text-slate-900 dark:text-white">{u.full_name}</p>
                                  <p className="text-sm text-slate-500">{u.email}</p>
                                </div>
                              </div>
                            </td>
                            <td className="p-4">
                              <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                                u.user_type === 'driver' 
                                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' 
                                  : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                              }`}>
                                {u.user_type}
                              </span>
                            </td>
                            <td className="p-4">
                              <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${status.color}`}>
                                {status.label}
                              </span>
                            </td>
                            <td className="p-4 text-slate-500 text-sm">
                              {new Date(u.created_at).toLocaleDateString()}
                            </td>
                            <td className="p-4">
                              <div className="flex items-center gap-2">
                                {u.status === 'banned' || u.status === 'suspended' || u.is_banned || u.is_suspended ? (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => openUserAction('reactivate', u)}
                                    className="gap-1.5 text-green-600 border-green-600 hover:bg-green-50 dark:hover:bg-green-900/20"
                                    data-testid={`reactivate-user-${u.id}`}
                                  >
                                    <ShieldCheck className="w-4 h-4" />
                                    Reactivate
                                  </Button>
                                ) : (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => openUserAction('suspend', u)}
                                      className="gap-1.5 text-amber-600 border-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                                      data-testid={`suspend-user-${u.id}`}
                                    >
                                      <ShieldOff className="w-4 h-4" />
                                      Suspend
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => openUserAction('ban', u)}
                                      className="gap-1.5 text-red-600 border-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                                      data-testid={`ban-user-${u.id}`}
                                    >
                                      <Ban className="w-4 h-4" />
                                      Ban
                                    </Button>
                                  </>
                                )}
                                {!u.is_admin && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => openUserAction('delete', u)}
                                    className="gap-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                                    data-testid={`delete-user-${u.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {filteredUsers.length === 0 && (
                        <tr>
                          <td colSpan={5} className="p-8 text-center text-slate-500">
                            No users found matching your criteria.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Admins Tab */}
        {activeTab === 'admins' && (
          <div className="space-y-6">
            {/* Admins Header */}
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white">Admin Management</h2>
                <p className="text-sm text-slate-500">{admins.length} admin users</p>
              </div>
            </div>

            {/* Grant Admin Access Card */}
            <Card className="bg-white dark:bg-slate-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserCog className="w-5 h-5 text-green-500" />
                  Grant Admin Access
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                      User ID
                    </label>
                    <Input
                      placeholder="Enter user ID to grant admin access..."
                      value={grantAdminUserId}
                      onChange={(e) => setGrantAdminUserId(e.target.value)}
                      className="bg-white dark:bg-slate-700"
                      data-testid="grant-admin-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Find the user ID from the Users tab
                    </p>
                  </div>
                  <div className="flex items-end">
                    <Button
                      onClick={handleGrantAdmin}
                      disabled={processingAction || !grantAdminUserId}
                      className="bg-green-600 hover:bg-green-700 gap-2"
                      data-testid="grant-admin-btn"
                    >
                      {processingAction ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crown className="w-4 h-4" />}
                      Grant Admin
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Admin Users List */}
            <Card className="bg-white dark:bg-slate-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Crown className="w-5 h-5 text-amber-500" />
                  Admin Users
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200 dark:border-slate-700">
                        <th className="text-left p-4 text-sm font-medium text-slate-500">Admin</th>
                        <th className="text-left p-4 text-sm font-medium text-slate-500">Source</th>
                        <th className="text-left p-4 text-sm font-medium text-slate-500">User Type</th>
                        <th className="text-left p-4 text-sm font-medium text-slate-500">Actions Taken</th>
                        <th className="text-left p-4 text-sm font-medium text-slate-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {admins.map((admin) => {
                        const sourceBadge = getAdminSourceBadge(admin.admin_source);
                        return (
                          <tr key={admin.id} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700/50">
                            <td className="p-4">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                                  <Crown className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                                </div>
                                <div>
                                  <p className="font-medium text-slate-900 dark:text-white">{admin.full_name || 'Admin User'}</p>
                                  <p className="text-sm text-slate-500">{admin.email}</p>
                                </div>
                              </div>
                            </td>
                            <td className="p-4">
                              <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${sourceBadge.color}`}>
                                {sourceBadge.label}
                              </span>
                            </td>
                            <td className="p-4">
                              <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300">
                                {admin.user_type || 'admin'}
                              </span>
                            </td>
                            <td className="p-4 text-slate-600 dark:text-slate-400">
                              {admin.admin_actions_count || 0} actions
                            </td>
                            <td className="p-4">
                              {admin.admin_source !== 'environment' && admin.admin_source !== 'both' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setAdminActionModal({ show: true, type: 'revoke', admin })}
                                  className="gap-1.5 text-red-600 border-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                                  data-testid={`revoke-admin-${admin.id}`}
                                >
                                  <ShieldOff className="w-4 h-4" />
                                  Revoke
                                </Button>
                              )}
                              {(admin.admin_source === 'environment' || admin.admin_source === 'both') && (
                                <span className="text-xs text-slate-500">Protected</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {admins.length === 0 && (
                        <tr>
                          <td colSpan={5} className="p-8 text-center text-slate-500">
                            No admin users found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Admin Activity Log */}
            <Card className="bg-white dark:bg-slate-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="w-5 h-5 text-blue-500" />
                  Recent Admin Activity
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200 dark:border-slate-700">
                        <th className="text-left p-4 text-sm font-medium text-slate-500">Admin</th>
                        <th className="text-left p-4 text-sm font-medium text-slate-500">Action</th>
                        <th className="text-left p-4 text-sm font-medium text-slate-500">Target</th>
                        <th className="text-left p-4 text-sm font-medium text-slate-500">Reason</th>
                        <th className="text-left p-4 text-sm font-medium text-slate-500">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {adminActivity.slice(0, 20).map((activity, index) => (
                        <tr key={index} className="border-b border-slate-100 dark:border-slate-800">
                          <td className="p-4 text-slate-900 dark:text-white font-medium">
                            {activity.admin_name || 'Unknown Admin'}
                          </td>
                          <td className="p-4">
                            <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                              activity.action === 'ban' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                              activity.action === 'suspend' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                              activity.action === 'reactivate' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                              activity.action === 'delete' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                              activity.action === 'grant_admin' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' :
                              activity.action === 'revoke_admin' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
                              'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300'
                            }`}>
                              {activity.action}
                            </span>
                          </td>
                          <td className="p-4 text-slate-600 dark:text-slate-400">
                            {activity.user_email || activity.user_id}
                          </td>
                          <td className="p-4 text-slate-500 text-sm max-w-xs truncate">
                            {activity.reason || '-'}
                          </td>
                          <td className="p-4 text-slate-500 text-sm">
                            {new Date(activity.created_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                      {adminActivity.length === 0 && (
                        <tr>
                          <td colSpan={5} className="p-8 text-center text-slate-500">
                            No admin activity recorded yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Rides Tab */}
        {activeTab === 'rides' && (
          <Card className="bg-white dark:bg-slate-800">
            <CardHeader>
              <CardTitle>Recent Rides</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-slate-700">
                      <th className="text-left p-3 text-sm font-medium text-slate-500">ID</th>
                      <th className="text-left p-3 text-sm font-medium text-slate-500">Status</th>
                      <th className="text-left p-3 text-sm font-medium text-slate-500">Fare</th>
                      <th className="text-left p-3 text-sm font-medium text-slate-500">Distance</th>
                      <th className="text-left p-3 text-sm font-medium text-slate-500">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rides.map((ride) => (
                      <tr key={ride.id} className="border-b border-slate-100 dark:border-slate-800">
                        <td className="p-3 font-mono text-sm text-slate-600">
                          {ride.id.slice(0, 8)}...
                        </td>
                        <td className="p-3">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            ride.status === 'completed' ? 'bg-green-100 text-green-700' :
                            ride.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                            ride.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {ride.status}
                          </span>
                        </td>
                        <td className="p-3 font-medium text-slate-900 dark:text-white">
                          ${(ride.actual_fare || ride.estimated_fare)?.toFixed(2)}
                        </td>
                        <td className="p-3 text-slate-600 dark:text-slate-400">
                          {ride.distance_miles?.toFixed(1)} mi
                        </td>
                        <td className="p-3 text-slate-500 text-sm">
                          {new Date(ride.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                Document Verification
              </h2>
              <span className="text-sm text-slate-500">
                {pendingDocuments.length} pending review
              </span>
            </div>

            {pendingDocuments.length === 0 ? (
              <Card className="bg-white dark:bg-slate-800">
                <CardContent className="py-12 text-center">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                    All Caught Up!
                  </h3>
                  <p className="text-slate-500">
                    No documents pending review at the moment.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {pendingDocuments.map((doc) => (
                  <Card key={doc.id} className="bg-white dark:bg-slate-800">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-4 flex-1">
                          <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-xl">
                            <FileText className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-slate-900 dark:text-white">
                                {doc.document_name}
                              </h3>
                              <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 rounded-full">
                                Pending
                              </span>
                            </div>
                            <div className="text-sm text-slate-600 dark:text-slate-400 space-y-1">
                              <p><strong>Driver:</strong> {doc.driver_name || 'Unknown'} ({doc.driver_email})</p>
                              {doc.vehicle_info && <p><strong>Vehicle:</strong> {doc.vehicle_info}</p>}
                              <p><strong>Uploaded:</strong> {new Date(doc.uploaded_at).toLocaleString()}</p>
                              <p><strong>File:</strong> {doc.file_name}</p>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => viewDocument(doc.id)}
                            className="gap-1"
                            data-testid={`view-doc-${doc.id}`}
                          >
                            <Eye className="w-4 h-4" />
                            View
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleReviewDocument(doc.id, 'approve')}
                            className="bg-green-600 hover:bg-green-700 gap-1"
                            data-testid={`approve-doc-${doc.id}`}
                          >
                            <CheckCircle className="w-4 h-4" />
                            Approve
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleRejectDocument(doc.id)}
                            className="gap-1"
                            data-testid={`reject-doc-${doc.id}`}
                          >
                            <XCircle className="w-4 h-4" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Promos Tab */}
        {activeTab === 'promos' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">Promo Codes</h2>
              <Button onClick={() => setShowCreatePromo(true)} className="bg-red-600 hover:bg-red-700">
                <Plus className="w-4 h-4 mr-2" />
                Create Promo
              </Button>
            </div>

            {showCreatePromo && (
              <Card className="bg-white dark:bg-slate-800">
                <CardHeader>
                  <CardTitle>Create Promo Code</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleCreatePromo} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium mb-1 block">Code</label>
                        <Input
                          value={promoForm.code}
                          onChange={(e) => setPromoForm({ ...promoForm, code: e.target.value.toUpperCase() })}
                          placeholder="SAVE20"
                          required
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-1 block">Discount Type</label>
                        <select
                          value={promoForm.discount_type}
                          onChange={(e) => setPromoForm({ ...promoForm, discount_type: e.target.value })}
                          className="w-full p-2 border rounded-lg"
                        >
                          <option value="percentage">Percentage</option>
                          <option value="fixed">Fixed Amount</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-1 block">
                          Discount Value {promoForm.discount_type === 'percentage' ? '(%)' : '($)'}
                        </label>
                        <Input
                          type="number"
                          value={promoForm.discount_value}
                          onChange={(e) => setPromoForm({ ...promoForm, discount_value: parseFloat(e.target.value) })}
                          min={0}
                          required
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-1 block">Max Uses</label>
                        <Input
                          type="number"
                          value={promoForm.max_uses}
                          onChange={(e) => setPromoForm({ ...promoForm, max_uses: parseInt(e.target.value) })}
                          min={1}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">Description</label>
                      <Input
                        value={promoForm.description}
                        onChange={(e) => setPromoForm({ ...promoForm, description: e.target.value })}
                        placeholder="20% off for new riders"
                      />
                    </div>
                    <div className="flex gap-3">
                      <Button type="submit" disabled={submitting}>
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
                      </Button>
                      <Button type="button" variant="outline" onClick={() => setShowCreatePromo(false)}>
                        Cancel
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            )}

            <Card className="bg-white dark:bg-slate-800">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200 dark:border-slate-700">
                        <th className="text-left p-4 text-sm font-medium text-slate-500">Code</th>
                        <th className="text-left p-4 text-sm font-medium text-slate-500">Discount</th>
                        <th className="text-left p-4 text-sm font-medium text-slate-500">Uses</th>
                        <th className="text-left p-4 text-sm font-medium text-slate-500">Status</th>
                        <th className="text-left p-4 text-sm font-medium text-slate-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {promos.map((promo) => (
                        <tr key={promo.id} className="border-b border-slate-100 dark:border-slate-800">
                          <td className="p-4">
                            <span className="font-mono font-bold text-red-600">{promo.code}</span>
                            {promo.description && (
                              <p className="text-xs text-slate-500 mt-1">{promo.description}</p>
                            )}
                          </td>
                          <td className="p-4">
                            {promo.discount_type === 'percentage' 
                              ? `${promo.discount_value}%` 
                              : `$${promo.discount_value}`}
                          </td>
                          <td className="p-4">
                            {promo.current_uses} / {promo.max_uses || '∞'}
                          </td>
                          <td className="p-4">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              promo.is_active 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-slate-100 text-slate-500'
                            }`}>
                              {promo.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="p-4">
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleTogglePromo(promo.id, promo.is_active)}
                              >
                                {promo.is_active ? 'Deactivate' : 'Activate'}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeletePromo(promo.id)}
                                className="text-red-500"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Payouts Tab */}
        {activeTab === 'payouts' && (
          <Card className="bg-white dark:bg-slate-800">
            <CardHeader>
              <CardTitle>Payout Requests</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-slate-700">
                      <th className="text-left p-3 text-sm font-medium text-slate-500">Driver</th>
                      <th className="text-left p-3 text-sm font-medium text-slate-500">Amount</th>
                      <th className="text-left p-3 text-sm font-medium text-slate-500">Bank</th>
                      <th className="text-left p-3 text-sm font-medium text-slate-500">Status</th>
                      <th className="text-left p-3 text-sm font-medium text-slate-500">Date</th>
                      <th className="text-left p-3 text-sm font-medium text-slate-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payouts.map((payout) => (
                      <tr key={payout.id} className="border-b border-slate-100 dark:border-slate-800">
                        <td className="p-3">
                          <p className="font-medium text-slate-900 dark:text-white">
                            {payout.driver?.full_name || 'Unknown'}
                          </p>
                          <p className="text-xs text-slate-500">{payout.driver?.email}</p>
                        </td>
                        <td className="p-3 font-bold text-slate-900 dark:text-white">
                          ${payout.amount.toFixed(2)}
                        </td>
                        <td className="p-3 text-slate-600 dark:text-slate-400">
                          {payout.bank_name} ****{payout.account_last4}
                        </td>
                        <td className="p-3">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            payout.status === 'completed' ? 'bg-green-100 text-green-700' :
                            payout.status === 'failed' ? 'bg-red-100 text-red-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {payout.status}
                          </span>
                        </td>
                        <td className="p-3 text-slate-500 text-sm">
                          {new Date(payout.created_at).toLocaleDateString()}
                        </td>
                        <td className="p-3">
                          {['pending', 'processing'].includes(payout.status) && (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => handleProcessPayout(payout.id, 'complete')}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleProcessPayout(payout.id, 'reject')}
                                className="text-red-500 border-red-500"
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            {/* Surge Pricing Settings */}
            <Card className="bg-white dark:bg-slate-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-amber-500" />
                  Surge Pricing Configuration
                </CardTitle>
              </CardHeader>
              <CardContent>
                {config?.surge_hours && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {Object.entries(config.surge_hours).map(([period, settings]) => (
                      <div key={period} className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl">
                        <h4 className="font-semibold text-slate-900 dark:text-white capitalize mb-2">
                          {period.replace('_', ' ')}
                        </h4>
                        <p className="text-sm text-slate-500">
                          {settings.start}:00 - {settings.end}:00
                        </p>
                        <p className="text-lg font-bold text-amber-600 mt-2">
                          {settings.multiplier}x multiplier
                        </p>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-sm text-slate-400 mt-4">
                  Configure surge hours in backend/.env file: SURGE_HOURS
                </p>
              </CardContent>
            </Card>

            {/* Admin Emails */}
            <Card className="bg-white dark:bg-slate-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserCheck className="w-5 h-5 text-blue-500" />
                  Admin Access
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-slate-900 dark:text-white mb-2">Configured Admin Emails</h4>
                    <div className="flex flex-wrap gap-2">
                      {config?.admin_emails?.map((email) => (
                        <span key={email} className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full text-sm">
                          {email}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
                    <h4 className="font-medium text-slate-900 dark:text-white mb-2">Add New Admin</h4>
                    <p className="text-sm text-slate-500 mb-4">
                      New admins can register at /admin using the admin secret key
                    </p>
                    <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl">
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        <strong>Environment Variables:</strong>
                      </p>
                      <code className="block mt-2 p-2 bg-slate-200 dark:bg-slate-800 rounded text-sm font-mono">
                        ADMIN_EMAILS=email1@test.com,email2@test.com
                      </code>
                      <code className="block mt-2 p-2 bg-slate-200 dark:bg-slate-800 rounded text-sm font-mono">
                        ADMIN_SECRET_KEY=your_secret_key_here
                      </code>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Driver Tiers */}
            <Card className="bg-white dark:bg-slate-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-500" />
                  Driver Loyalty Tiers
                </CardTitle>
              </CardHeader>
              <CardContent>
                {config?.driver_tiers && (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200 dark:border-slate-700">
                          <th className="text-left p-3 text-sm font-medium text-slate-500">Tier</th>
                          <th className="text-left p-3 text-sm font-medium text-slate-500">Min Rides</th>
                          <th className="text-left p-3 text-sm font-medium text-slate-500">Bonus</th>
                          <th className="text-left p-3 text-sm font-medium text-slate-500">Perks</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(config.driver_tiers).map(([tier, settings]) => (
                          <tr key={tier} className="border-b border-slate-100 dark:border-slate-800">
                            <td className="p-3 font-medium text-slate-900 dark:text-white capitalize">{tier}</td>
                            <td className="p-3 text-slate-600 dark:text-slate-400">{settings.min_rides}</td>
                            <td className="p-3 text-green-600">{(settings.bonus_percent * 100).toFixed(0)}%</td>
                            <td className="p-3 text-sm text-slate-500">
                              {settings.perks?.join(', ') || 'None'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* User Action Modal */}
      {userActionModal.show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="user-action-modal">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
              <div className="flex items-center gap-3">
                {userActionModal.type === 'suspend' && <ShieldOff className="w-6 h-6 text-amber-500" />}
                {userActionModal.type === 'ban' && <Ban className="w-6 h-6 text-red-500" />}
                {userActionModal.type === 'reactivate' && <ShieldCheck className="w-6 h-6 text-green-500" />}
                {userActionModal.type === 'delete' && <Trash2 className="w-6 h-6 text-red-500" />}
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white capitalize">
                  {userActionModal.type} User
                </h3>
              </div>
              <button 
                onClick={closeUserAction}
                className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
                data-testid="close-user-modal"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* User Info */}
              <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                <div className="w-12 h-12 rounded-full bg-slate-200 dark:bg-slate-600 flex items-center justify-center">
                  <span className="text-lg font-semibold text-slate-600 dark:text-slate-300">
                    {userActionModal.user?.full_name?.charAt(0)?.toUpperCase() || '?'}
                  </span>
                </div>
                <div>
                  <p className="font-medium text-slate-900 dark:text-white">{userActionModal.user?.full_name}</p>
                  <p className="text-sm text-slate-500">{userActionModal.user?.email}</p>
                </div>
              </div>

              {/* Warning for Ban */}
              {userActionModal.type === 'ban' && (
                <div className="flex items-start gap-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-red-700 dark:text-red-400">
                    <p className="font-medium">Warning: This action is severe</p>
                    <p>Banning a user will permanently restrict their access. This should only be used for serious violations.</p>
                  </div>
                </div>
              )}

              {/* Suspend Form */}
              {userActionModal.type === 'suspend' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                      Suspension Duration (Days)
                    </label>
                    <Input
                      type="number"
                      min={1}
                      max={365}
                      value={actionForm.duration_days}
                      onChange={(e) => setActionForm({ ...actionForm, duration_days: parseInt(e.target.value) || 7 })}
                      data-testid="suspend-duration-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                      Reason for Suspension *
                    </label>
                    <textarea
                      className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white resize-none"
                      rows={3}
                      value={actionForm.reason}
                      onChange={(e) => setActionForm({ ...actionForm, reason: e.target.value })}
                      placeholder="Explain why this user is being suspended..."
                      data-testid="suspend-reason-input"
                    />
                  </div>
                </>
              )}

              {/* Ban Form */}
              {userActionModal.type === 'ban' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    Reason for Ban *
                  </label>
                  <textarea
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white resize-none"
                    rows={3}
                    value={actionForm.reason}
                    onChange={(e) => setActionForm({ ...actionForm, reason: e.target.value })}
                    placeholder="Explain why this user is being banned..."
                    data-testid="ban-reason-input"
                  />
                </div>
              )}

              {/* Reactivate Form */}
              {userActionModal.type === 'reactivate' && (
                <>
                  <div className="flex items-start gap-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                    <ShieldCheck className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-green-700 dark:text-green-400">
                      <p className="font-medium">Reactivate Account</p>
                      <p>This will restore the user's access to the platform.</p>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                      Notes (Optional)
                    </label>
                    <textarea
                      className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white resize-none"
                      rows={2}
                      value={actionForm.notes}
                      onChange={(e) => setActionForm({ ...actionForm, notes: e.target.value })}
                      placeholder="Add any notes about this reactivation..."
                      data-testid="reactivate-notes-input"
                    />
                  </div>
                </>
              )}

              {/* Delete Form */}
              {userActionModal.type === 'delete' && (
                <>
                  <div className="flex items-start gap-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-red-700 dark:text-red-400">
                      <p className="font-medium">Warning: This action is irreversible!</p>
                      <p>Deleting a user will permanently remove their account and all associated data. This cannot be undone.</p>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                      Reason for Deletion *
                    </label>
                    <textarea
                      className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white resize-none"
                      rows={3}
                      value={actionForm.reason}
                      onChange={(e) => setActionForm({ ...actionForm, reason: e.target.value })}
                      placeholder="Explain why this user account is being deleted..."
                      data-testid="delete-reason-input"
                    />
                  </div>
                </>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 p-4 border-t border-slate-200 dark:border-slate-700">
              <Button
                variant="outline"
                onClick={closeUserAction}
                disabled={processingAction}
                data-testid="cancel-user-action"
              >
                Cancel
              </Button>
              {userActionModal.type === 'suspend' && (
                <Button
                  onClick={handleSuspendUser}
                  disabled={processingAction || !actionForm.reason.trim()}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="confirm-suspend-btn"
                >
                  {processingAction ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldOff className="w-4 h-4 mr-2" />}
                  Suspend User
                </Button>
              )}
              {userActionModal.type === 'ban' && (
                <Button
                  onClick={handleBanUser}
                  disabled={processingAction || !actionForm.reason.trim()}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="confirm-ban-btn"
                >
                  {processingAction ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Ban className="w-4 h-4 mr-2" />}
                  Ban User
                </Button>
              )}
              {userActionModal.type === 'reactivate' && (
                <Button
                  onClick={handleReactivateUser}
                  disabled={processingAction}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="confirm-reactivate-btn"
                >
                  {processingAction ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                  Reactivate User
                </Button>
              )}
              {userActionModal.type === 'delete' && (
                <Button
                  onClick={handleDeleteUser}
                  disabled={processingAction || !actionForm.reason.trim()}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="confirm-delete-btn"
                >
                  {processingAction ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                  Delete Permanently
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Admin Action Modal - Revoke Admin */}
      {adminActionModal.show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="admin-action-modal">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
              <div className="flex items-center gap-3">
                <ShieldOff className="w-6 h-6 text-red-500" />
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                  Revoke Admin Access
                </h3>
              </div>
              <button 
                onClick={() => {
                  setAdminActionModal({ show: false, type: '', admin: null });
                  setRevokeReason('');
                }}
                className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
                data-testid="close-admin-modal"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Admin Info */}
              <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <Crown className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <p className="font-medium text-slate-900 dark:text-white">{adminActionModal.admin?.full_name || 'Admin User'}</p>
                  <p className="text-sm text-slate-500">{adminActionModal.admin?.email}</p>
                </div>
              </div>

              {/* Warning */}
              <div className="flex items-start gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-700 dark:text-amber-400">
                  <p className="font-medium">Revoking Admin Access</p>
                  <p>This user will lose all admin privileges and be demoted to their previous user type.</p>
                </div>
              </div>

              {/* Reason Input */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                  Reason for Revocation *
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white resize-none"
                  rows={3}
                  value={revokeReason}
                  onChange={(e) => setRevokeReason(e.target.value)}
                  placeholder="Explain why admin access is being revoked..."
                  data-testid="revoke-reason-input"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 p-4 border-t border-slate-200 dark:border-slate-700">
              <Button
                variant="outline"
                onClick={() => {
                  setAdminActionModal({ show: false, type: '', admin: null });
                  setRevokeReason('');
                }}
                disabled={processingAction}
                data-testid="cancel-admin-action"
              >
                Cancel
              </Button>
              <Button
                onClick={handleRevokeAdmin}
                disabled={processingAction || !revokeReason.trim()}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-revoke-btn"
              >
                {processingAction ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldOff className="w-4 h-4 mr-2" />}
                Revoke Admin
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
